// First-class system states (UX spec 8.1). One title, one sentence, at most one action.
import type { ReactNode } from 'react';
import { AlertTriangle, Ban, CircleOff, CloudOff, Inbox, Lock, SearchX, Settings2, WifiOff } from 'lucide-react';
import { Icon } from './Icon.tsx';
import type { ErrorView } from '../lib/errorCopy.ts';

export type StateVariant = 'empty' | 'filtered' | 'unconfigured' | 'unavailable' | 'denied' | 'offline' | 'dependency' | 'error' | 'loading';

const ICONS = {
  empty: Inbox,
  filtered: SearchX,
  unconfigured: Settings2,
  unavailable: CircleOff,
  denied: Lock,
  offline: WifiOff,
  dependency: CloudOff,
  error: AlertTriangle,
  loading: Inbox,
} as const;

export interface StatePanelProps {
  variant: StateVariant;
  title: string;
  body?: ReactNode;
  action?: ReactNode;
  details?: { code?: string | null; requestId?: string | null; permission?: string | null } | null;
  compact?: boolean;
  headingLevel?: 2 | 3;
}

export function StatePanel({ variant, title, body, action, details, compact, headingLevel = 2 }: StatePanelProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const role = variant === 'error' ? 'alert' : variant === 'loading' ? 'status' : undefined;
  return (
    <section className={`state-panel state-${variant}${compact ? ' state-compact' : ''}`} role={role} aria-live={variant === 'loading' ? 'polite' : undefined} aria-busy={variant === 'loading' || undefined}>
      <span className="state-icon">
        <Icon icon={variant === 'denied' ? Lock : variant === 'unavailable' ? Ban : ICONS[variant]} size="1.5em" />
      </span>
      <Heading className="state-title">{title}</Heading>
      {body ? <p className="state-body">{body}</p> : null}
      {action ? <div className="state-action">{action}</div> : null}
      {details && (details.code || details.requestId || details.permission) ? (
        <details className="state-details">
          <summary>Details</summary>
          <dl>
            {details.code ? (
              <>
                <dt>Code</dt>
                <dd className="mono">{details.code}</dd>
              </>
            ) : null}
            {details.permission ? (
              <>
                <dt>Permission</dt>
                <dd className="mono">{details.permission}</dd>
              </>
            ) : null}
            {details.requestId ? (
              <>
                <dt>Reference</dt>
                <dd className="mono">{details.requestId}</dd>
              </>
            ) : null}
          </dl>
        </details>
      ) : null}
    </section>
  );
}

export function ErrorPanel({ error, onRetry, compact }: { error: ErrorView; onRetry?: () => void; compact?: boolean }) {
  return (
    <StatePanel
      variant={error.code === 'network_error' ? 'offline' : error.code === 'permission_denied' ? 'denied' : 'error'}
      title={error.title}
      body={error.detail || undefined}
      compact={compact ?? false}
      action={onRetry ? <button type="button" className="btn" onClick={onRetry}>Try again</button> : undefined}
      details={{ code: error.code, requestId: error.requestId }}
    />
  );
}

export function SkeletonRows({ rows = 5, label }: { rows?: number; label: string }) {
  return (
    <div className="skeleton-list" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div className="skeleton-row" key={i} aria-hidden="true">
          <span className="skeleton-line w60" />
          <span className="skeleton-line w40" />
        </div>
      ))}
    </div>
  );
}
